import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info, RefreshCw, Shield, Users } from "lucide-react";
import { ROUTES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import api, { authApi, accessAdminApi, type AccessGroupDto } from "@/services/api";
import type { AppAccessMap } from "@/lib/appAccess";
import { cn } from "@/utils/cn";
import { extractCcOptions, type UserRow } from "./accessUtils";
import { GroupsTab } from "./GroupsTab";
import { UsersTab } from "./UsersTab";
import type { OperadorCatalogDto } from "@/services/locacoes";

type AdminTab = "groups" | "users";

export function AdminAcessoPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>("groups");
  const [userGroupFilter, setUserGroupFilter] = useState<string | null>(null);

  const [groups, setGroups] = useState<AccessGroupDto[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [allCcOptions, setAllCcOptions] = useState(extractCcOptions([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [savingGroup, setSavingGroup] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [savingCcUserId, setSavingCcUserId] = useState<string | null>(null);
  const [savingGroupUserId, setSavingGroupUserId] = useState<string | null>(null);
  const [savingPhoneUserId, setSavingPhoneUserId] = useState<string | null>(null);
  const [savingDisplayNameUserId, setSavingDisplayNameUserId] = useState<string | null>(null);
  const [savingPasswordUserId, setSavingPasswordUserId] = useState<string | null>(null);
  const [savingRoleUserId, setSavingRoleUserId] = useState<string | null>(null);
  const [operadores, setOperadores] = useState<OperadorCatalogDto[]>([]);
  const [operadoresLoading, setOperadoresLoading] = useState(false);
  const [operadoresError, setOperadoresError] = useState<string | null>(null);

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const [gr, us, cc] = await Promise.all([
        accessAdminApi.listGroups(),
        authApi.listUsers(),
        api.get("/api/cost-centers"),
      ]);
      if (gr.data.success) setGroups(gr.data.groups);
      if (us.data.success) {
        setUsers(
          us.data.users.map((u) => ({
            id: u.id,
            username: u.username,
            displayName: u.displayName,
            role: u.role,
            groupIds: u.groupIds ?? (u.groupId ? [u.groupId] : []),
            costCenterIds: u.costCenterIds,
            phone: u.phone ?? "",
            isTi: u.isTi === true,
            locacoesOperador: u.locacoesOperador,
            isOperadorOnly: u.isOperadorOnly,
            operadorCatalogId: u.operadorCatalogId ?? null,
            operadorCatalogNome: u.operadorCatalogNome ?? null,
            operadorContratosAtivos: u.operadorContratosAtivos ?? 0,
          })),
        );
      }
      setAllCcOptions(extractCcOptions(cc.data));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : "Erro ao carregar dados.");
      setError(msg);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadOperadores = useCallback(async () => {
    setOperadoresLoading(true);
    setOperadoresError(null);
    try {
      const { data } = await authApi.listOperadoresCatalog();
      if (!data.success) throw new Error(data.error ?? "Falha ao carregar operadores.");
      setOperadores(data.operadores ?? []);
    } catch (e: unknown) {
      setOperadores([]);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setOperadoresError(msg);
    } finally {
      setOperadoresLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "users") void loadOperadores();
  }, [activeTab, loadOperadores]);

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.id, label: g.name })),
    [groups],
  );

  const createGroup = async (name: string, access: AppAccessMap) => {
    setSavingGroup(true);
    setError(null);
    try {
      const { data } = await accessAdminApi.createGroup({ name, access });
      if (!data.success) throw new Error("Falha ao criar grupo.");
      await refresh();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      throw e;
    } finally {
      setSavingGroup(false);
    }
  };

  const updateGroup = async (id: string, name: string, access: AppAccessMap) => {
    setSavingGroup(true);
    setError(null);
    try {
      const { data } = await accessAdminApi.updateGroup(id, { name, access });
      if (!data.success) throw new Error("Falha ao salvar grupo.");
      await refresh();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      throw e;
    } finally {
      setSavingGroup(false);
    }
  };

  const removeGroup = async (id: string, memberCount: number) => {
    if (memberCount > 0) {
      setError(`Existem ${memberCount} usuário(s) neste grupo. Reatribua antes de excluir.`);
      return;
    }
    if (!window.confirm("Excluir este grupo?")) return;
    setError(null);
    try {
      const { data } = await accessAdminApi.deleteGroup(id);
      if (!data.success) throw new Error((data as { error?: string }).error ?? "Falha ao excluir.");
      await refresh();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
    }
  };

  const createUser = async (payload: {
    username: string;
    password: string;
    displayName: string;
    role: string;
    groupIds: string[];
    costCenterIds: string[];
    phone: string;
    isTi: boolean;
    operadorCatalogId?: string;
  }) => {
    setSavingUser(true);
    setError(null);
    try {
      const isAdmin = payload.role === "admin";
      const isOperador = payload.role === "operador";
      const { data } = await authApi.createUser({
        username: payload.username.trim(),
        password: payload.password,
        displayName: payload.displayName.trim() || payload.username.trim(),
        role: payload.role,
        groupIds: isAdmin || isOperador ? [] : payload.groupIds,
        costCenterIds: isAdmin ? undefined : isOperador ? [] : payload.costCenterIds,
        phone: payload.phone.trim() || undefined,
        isTi: payload.isTi,
        operadorCatalogId: isOperador ? payload.operadorCatalogId : undefined,
      });
      if (!data.success) throw new Error(data.error ?? "Falha ao criar usuário.");
      await Promise.all([refresh(), loadOperadores()]);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      throw e;
    } finally {
      setSavingUser(false);
    }
  };

  const updateUserRole = async (id: string, role: string, operadorCatalogId?: string | null) => {
    setError(null);
    setSavingRoleUserId(id);
    try {
      const { data } = await authApi.updateUser(id, {
        role,
        ...(operadorCatalogId !== undefined ? { operadorCatalogId } : {}),
      });
      if (!data.success) throw new Error(data.error ?? "Falha ao atualizar.");
      await Promise.all([refresh({ silent: true }), loadOperadores()]);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
      throw e;
    } finally {
      setSavingRoleUserId(null);
    }
  };

  const updateUserGroups = async (id: string, groupIds: string[]) => {
    setError(null);
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, groupIds: [...groupIds] } : u)),
    );
    setSavingGroupUserId(id);
    try {
      const { data } = await authApi.updateUser(id, { groupIds });
      if (!data.success) throw new Error(data.error ?? "Falha ao salvar grupos.");
      const saved = data.user?.groupIds;
      if (Array.isArray(saved)) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, groupIds: saved } : u)),
        );
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
    } finally {
      setSavingGroupUserId(null);
    }
  };

  const updateUserCostCenters = async (id: string, costCenterIds: string[]) => {
    setError(null);
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, costCenterIds: [...costCenterIds] } : u)),
    );
    setSavingCcUserId(id);
    try {
      const { data } = await authApi.updateUser(id, { costCenterIds });
      if (!data.success) throw new Error(data.error ?? "Falha ao salvar obras.");
      const saved = data.user?.costCenterIds;
      if (Array.isArray(saved)) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, costCenterIds: saved } : u)),
        );
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
    } finally {
      setSavingCcUserId(null);
    }
  };

  const updateUserPhone = async (id: string, phone: string) => {
    setError(null);
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, phone } : u)));
    setSavingPhoneUserId(id);
    try {
      const { data } = await authApi.updateUser(id, { phone: phone.trim() || null });
      if (!data.success) throw new Error(data.error ?? "Falha ao salvar telefone.");
      await refresh({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
    } finally {
      setSavingPhoneUserId(null);
    }
  };

  const updateUserTi = async (id: string, isTi: boolean) => {
    setError(null);
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, isTi } : u)));
    try {
      const { data } = await authApi.updateUser(id, { isTi });
      if (!data.success) throw new Error(data.error ?? "Falha ao atualizar equipe de TI.");
      await refresh({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
    }
  };

  const updateUserDisplayName = async (id: string, displayName: string) => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError("Informe o nome exibido do usuário.");
      return;
    }
    setError(null);
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, displayName: trimmed } : u)));
    setSavingDisplayNameUserId(id);
    try {
      const { data } = await authApi.updateUser(id, { displayName: trimmed });
      if (!data.success) throw new Error(data.error ?? "Falha ao salvar nome.");
      await refresh({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      await refresh({ silent: true });
    } finally {
      setSavingDisplayNameUserId(null);
    }
  };

  const updateUserPassword = async (id: string, password: string) => {
    if (password.length < 6) {
      setError("A senha deve ter no mínimo 6 caracteres.");
      throw new Error("A senha deve ter no mínimo 6 caracteres.");
    }
    setError(null);
    setSavingPasswordUserId(id);
    try {
      const { data } = await authApi.updateUser(id, { password });
      if (!data.success) throw new Error(data.error ?? "Falha ao alterar senha.");
      await refresh({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
      throw e;
    } finally {
      setSavingPasswordUserId(null);
    }
  };

  const deleteUser = async (id: string) => {
    if (!window.confirm("Excluir este usuário?")) return;
    setError(null);
    try {
      const { data } = await authApi.deleteUser(id);
      if (!data.success) throw new Error(data.error ?? "Falha ao excluir.");
      await refresh();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : String(e));
      setError(msg);
    }
  };

  const handleViewMembers = (groupId: string) => {
    setUserGroupFilter(groupId);
    setActiveTab("users");
  };

  const tabs: { id: AdminTab; label: string; icon: typeof Shield }[] = [
    { id: "groups", label: "Grupos de acesso", icon: Shield },
    { id: "users", label: "Usuários", icon: Users },
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50 text-slate-800">
      <header className="z-10 flex flex-shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 md:px-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.HOME)}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-brand-600" />
          <h1 className="text-lg font-bold">Acessos</h1>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => refresh()}
          loading={loading}
          leftIcon={<RefreshCw className="w-4 h-4" />}
          className="ml-auto"
        >
          Atualizar
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="w-full space-y-6 px-4 py-4 md:px-6 md:py-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}

        <div className="rounded-xl border border-brand-100 bg-brand-50/40 px-4 py-3 flex gap-3">
          <Info className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 space-y-1">
            <p className="font-medium text-slate-800">Como funciona</p>
            <ol className="list-decimal list-inside text-xs text-slate-600 space-y-0.5">
              <li>
                <strong className="font-medium">Grupos</strong> definem quais módulos aparecem no menu.
              </li>
              <li>
                <strong className="font-medium">Usuários</strong> recebem um ou mais grupos — as permissões
                somam (união).
              </li>
              <li>
                <strong className="font-medium">Obras</strong> limitam quais centros de custo cada usuário
                enxerga nos dados.
              </li>
              <li>
                <strong className="font-medium">Operadores</strong> são contas de campo vinculadas ao
                cadastro em Locações — acessam só o app <code className="text-[11px]">/operador</code>.
              </li>
              <li>
                <strong className="font-medium">Administradores</strong> ignoram grupos e obras — acesso
                total.
              </li>
            </ol>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-card overflow-hidden">
          <div className="flex border-b border-slate-200">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={cn(
                  "flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px",
                  activeTab === id
                    ? "border-brand-600 text-brand-700 bg-brand-50/50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {loading ? (
              <p className="text-slate-500 text-sm py-8 text-center">Carregando…</p>
            ) : activeTab === "groups" ? (
              <GroupsTab
                groups={groups}
                users={users}
                savingGroup={savingGroup}
                onCreateGroup={createGroup}
                onUpdateGroup={updateGroup}
                onDeleteGroup={removeGroup}
                onViewMembers={handleViewMembers}
              />
            ) : (
              <UsersTab
                users={users}
                groups={groups}
                groupOptions={groupOptions}
                allCcOptions={allCcOptions}
                loading={loading}
                initialGroupFilter={userGroupFilter}
                savingUser={savingUser}
                savingCcUserId={savingCcUserId}
                savingGroupUserId={savingGroupUserId}
                savingPhoneUserId={savingPhoneUserId}
                savingDisplayNameUserId={savingDisplayNameUserId}
                savingPasswordUserId={savingPasswordUserId}
                savingRoleUserId={savingRoleUserId}
                operadores={operadores}
                operadoresLoading={operadoresLoading}
                operadoresError={operadoresError}
                onReloadOperadores={loadOperadores}
                onCreateUser={createUser}
                onUpdateRole={updateUserRole}
                onUpdateGroups={updateUserGroups}
                onUpdateCostCenters={updateUserCostCenters}
                onUpdatePhone={updateUserPhone}
                onUpdateTi={updateUserTi}
                onUpdateDisplayName={updateUserDisplayName}
                onUpdatePassword={updateUserPassword}
                onDeleteUser={deleteUser}
              />
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
